import { ContentLink } from "@robe/client/xcore-ui";
import { Box, Collapse, Flex, Icon, Link as UILink, Stack, Typography, useModal } from "@xcorejs/ui";
import DropIcon8 from "components/Icons/8/drop.svg";
import { userLogout } from "components/Layout/LoggedBar";
import { useState, Dispatch, SetStateAction } from "react";
import { useUser } from "hooks/useUser";
import { useLayout } from "xcore";
import { HeaderState } from ".";
import NavItem, { SubNavLinkStyle } from "./NavItem";
import NavItems from "./NavItems";
import { useHeaderMenu } from "./useHeaderMenu";
import { useRevalidateCache } from "../../../pages/_app";
import { GetProductGridResult } from "pages/api/robe/product-grid";
import { UserLoginModal } from "components/User/UserLoginModal";
import ScrollLock from "design-system/ScrollLock";
import { useFrontendQuery } from "hooks/useFrontendQuery";

interface HeaderMiddleContentProps {
  state: HeaderState;
  setState: Dispatch<SetStateAction<HeaderState>>;
}

export const HeaderMiddleContent = ({ state, setState }: HeaderMiddleContentProps) => {
  const {
    general,
    stringify,
    core,
    header: {
      nav: { products },
    },
    userLoginPage,
    accountUpdate,
  } = useLayout();
  const [userDropdownActive, setUserDropdownActive] = useState(false);
  const { user } = useUser();

  const revalidate = useRevalidateCache();

  const menu = useHeaderMenu(products);
  const [open] = useModal(UserLoginModal);
  const { data: productGrid } = useFrontendQuery<GetProductGridResult>(`/api/robe/product-grid?locale=${core.currentLocale}`);

  return (
    <ScrollLock isActive={state === "menu" || state === "localeSwitcher"}>
      <Flex
        alignItems="stretch"
        justifyContent={{ _: "space-between", md: "center" }}
        height={{ _: "calc((var(--vh, 1vh) * 100) - 5rem)", md: "8.4rem" }}
        position={{ _: "absolute", md: "static" }}
        top={{ _: "5rem", md: "0" }}
        left={{ _: "0", md: "0" }}
        background={{ _: "#fff", md: "transparent" }}
        flexDirection={{ _: "column", md: "row" }}
        width={{ _: "100vw", md: "auto" }}
        display={state !== "menu" ? { _: "none", md: "flex" } : { _: "flex" }}
        overflow={{ _: "auto", md: "unset" }}
      >
        <NavItems>
          <NavItem {...menu.homepage} />
          <NavItem {...menu.spotOn} />
          <NavItem {...menu.products} productGrid={productGrid} />
          <NavItem {...menu.innovations} />
          <NavItem {...menu.support} />
          <NavItem {...menu.news} />
          {menu.sustainability && <NavItem {...menu.sustainability} />}
          <NavItem {...menu.aboutUs} setState={setState} />
          <NavItem {...menu.contacts} />
        </NavItems>

        {user ? (
          <Box display={{ _: "flex", md: "none" }} width="100%" position="relative">
            <Flex
              bg="lightGray"
              height="5rem"
              justifyContent="space-between"
              alignItems="center"
              px="2rem"
              width="100%"
              onClick={() => setUserDropdownActive(!userDropdownActive)}
            >
              <Flex>
                <Stack gap="0.4rem" alignItems="center">
                  <Typography color="black">{user.company}</Typography>
                </Stack>
              </Flex>
              <Flex alignItems="center">
                <Typography fontWeight={700} color="black" mx="0.8rem">
                  {user.firstName} {user.lastName}
                </Typography>

                <Icon
                  svg={<DropIcon8 />}
                  fill="#41414B"
                  transition="transform 300ms"
                  transform={userDropdownActive ? "rotate(-180deg)" : ""}
                />
              </Flex>
            </Flex>
            <Box bg="lightGray" position="absolute" bottom="5rem" width="100%">
              <Collapse open={userDropdownActive}>
                {general.values.accountUpdate.type && general.values.accountUpdate.slug && (
                  <ContentLink
                    content={{
                      id: general.values.accountUpdate.id,
                      type: general.values.accountUpdate.type,
                      slug: general.values.accountUpdate.slug,
                    }}
                    {...SubNavLinkStyle}
                    width="100%"
                    justifyContent="center"
                  >
                    {accountUpdate && stringify(accountUpdate.title)}
                  </ContentLink>
                )}
                <UILink {...SubNavLinkStyle} justifyContent="center" onClick={userLogout(core, revalidate)}>
                  Logout
                </UILink>
              </Collapse>
            </Box>
          </Box>
        ) : (
          <Flex bg="lightGray" height="5rem" display={{ _: "flex", md: "none" }}>
            <Typography
              display="flex"
              width="100%"
              height="100%"
              alignItems="center"
              justifyContent="center"
              color="text"
              fontWeight={700}
              onClick={open}
            >
              {userLoginPage && stringify(userLoginPage.values.userLoginHeading)}
            </Typography>
          </Flex>
        )}
      </Flex>
    </ScrollLock>
  );
};
